// Medical Report input modal.
//
// Most fields are optional — the report renders well even with just the
// recipient name. Doctors typically fill in:
//   - recipient (employer / insurer / school)
//   - purpose (fitness for work / school / sport / insurance claim)
//   - prognosis (their professional outlook)
//   - recommendations (what they want the recipient to do)
// The clinical content (history / exam / dx / treatment / follow-up) is
// pulled automatically from the encounter's CNE data — the doctor
// doesn't retype any of it.

import { useState } from 'react'
import Modal, { Field, ModalActions, PrimaryButton, SecondaryButton } from './Modal.jsx'

export default function MedicalReportModal({ onClose, onSubmit, submitting }) {
  const today = new Date().toISOString().slice(0, 10)
  const [recipientName, setRecipientName] = useState('To Whom It May Concern')
  const [recipientAddress, setRecipientAddress] = useState('')
  const [purpose, setPurpose] = useState('')
  const [examinationDate, setExaminationDate] = useState(today)
  const [includeDiagnosis, setIncludeDiagnosis] = useState(true)
  const [prognosis, setPrognosis] = useState('')
  const [recommendations, setRecommendations] = useState('')

  return (
    <Modal
      title="Medical Report"
      subtitle="Comprehensive clinical report addressed to a third party. Clinical sections auto-fill from the encounter."
      onClose={onClose}
      wide
    >
      <Field
        label="Addressed to"
        hint="Default is the standard 'To Whom It May Concern' opening."
      >
        <input
          type="text"
          value={recipientName}
          onChange={(e) => setRecipientName(e.target.value)}
          className="border-2 border-slate-200 focus:border-wantok-600 focus:outline-none rounded-lg px-3 py-2 text-base w-full"
          placeholder="e.g. Manager, ABC Logistics"
        />
      </Field>
      <Field label="Recipient address (optional)">
        <textarea
          rows={2}
          value={recipientAddress}
          onChange={(e) => setRecipientAddress(e.target.value)}
          className="border-2 border-slate-200 focus:border-wantok-600 focus:outline-none rounded-lg px-3 py-2 text-sm w-full"
          placeholder="Street&#10;City, Province"
        />
      </Field>

      <Field
        label="Purpose"
        hint="What this report is for. Appears at the top and shapes the opening sentence."
      >
        <input
          type="text"
          value={purpose}
          onChange={(e) => setPurpose(e.target.value)}
          className="border-2 border-slate-200 focus:border-wantok-600 focus:outline-none rounded-lg px-3 py-2 text-base w-full"
          placeholder="e.g. fitness-for-work assessment after workplace injury"
        />
      </Field>

      <Field label="Examination date">
        <input
          type="date"
          value={examinationDate}
          onChange={(e) => setExaminationDate(e.target.value)}
          className="border-2 border-slate-200 focus:border-wantok-600 focus:outline-none rounded-lg px-3 py-2 text-base"
        />
      </Field>

      <Field label="Privacy">
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={includeDiagnosis}
            onChange={(e) => setIncludeDiagnosis(e.target.checked)}
            className="w-4 h-4 accent-wantok-700"
          />
          <span className="text-sm text-slate-700">
            Include diagnosis (recommended for medical reports)
          </span>
        </label>
      </Field>

      <Field
        label="Prognosis (optional)"
        hint="Your professional outlook on recovery / fitness."
      >
        <textarea
          rows={2}
          value={prognosis}
          onChange={(e) => setPrognosis(e.target.value)}
          className="border-2 border-slate-200 focus:border-wantok-600 focus:outline-none rounded-lg px-3 py-2 text-sm w-full"
          placeholder="e.g. expected full recovery within six weeks with adherence to treatment"
        />
      </Field>

      <Field
        label="Recommendations (optional)"
        hint="What you want the recipient to do."
      >
        <textarea
          rows={2}
          value={recommendations}
          onChange={(e) => setRecommendations(e.target.value)}
          className="border-2 border-slate-200 focus:border-wantok-600 focus:outline-none rounded-lg px-3 py-2 text-sm w-full"
          placeholder="e.g. light duties for 4 weeks; reassess on review"
        />
      </Field>

      <div className="bg-slate-50 border-2 border-slate-100 rounded-lg p-3 text-xs text-slate-600">
        <strong className="font-bold text-slate-800">Auto-filled from this encounter:</strong>{' '}
        chief complaint, history of presenting illness, allergies, current
        medications, past medical history, examination findings,
        investigations performed, diagnoses, treatment plan, prescribed
        medications, and follow-up plan.
      </div>

      <ModalActions>
        <SecondaryButton onClick={onClose} disabled={submitting}>Cancel</SecondaryButton>
        <PrimaryButton
          onClick={() =>
            onSubmit({
              recipient_name: recipientName,
              recipient_address: recipientAddress,
              purpose,
              examination_date: examinationDate,
              include_diagnosis: includeDiagnosis,
              prognosis,
              recommendations,
            })
          }
          disabled={submitting}
        >
          {submitting ? 'Generating…' : 'Generate medical report'}
        </PrimaryButton>
      </ModalActions>
    </Modal>
  )
}
